import { useEffect, useState } from "react";
import { GridColDef, GridFilterModel, GridSortModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";

const useEPaper = (columns: GridColDef[]) => {
  const [ePaperData, setEPaperData] = useState<any[]>([]);
  const [selectedEPaperId, setSelectedEPaperId] = useState<string | null>(null);
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [sortingModel, setSortingModel] = useState<GridSortModel>();
  const [loading, setLoading] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // DataGrid is 0-indexed
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [paginationModel, filterModel, sortingModel]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const params = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        filters: filterModel?.items.map(({ field, operator, value }) => ({
          field,
          type: columns?.find((col) => col.field === field)?.type || "string",
          operator,
          value,
        })),
        sortBy: sortingModel?.[0]?.field || "date",
        sortOrder: sortingModel?.[0]?.sort || "desc",
      };

      const result = await ApiService({
        method: "GET",
        endpoint: apiPaths.getEPapers,
        params,
      });

      // Handle API response structure: { success: true, data: [...] }
      if (result?.success && Array.isArray(result?.data)) {
        setEPaperData(result.data || []);
        setRowCount(result.data.length || 0);
      } else if (Array.isArray(result)) {
        // Fallback for direct array response
        setEPaperData(result || []);
        setRowCount(result.length || 0);
      } else {
        setEPaperData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching E-paper data:", error);
      toast.error("Failed to fetch E-paper data");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id: string) => {
    setSelectedEPaperId(id);
    navigate(`/add-e-paper/${id}`);
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await ApiService({
        method: "DELETE",
        endpoint: `${apiPaths.deleteEPaper}/${id}`,
      });
      setEPaperData((prev) => prev.filter((item) => item._id !== id));
      toast.success("E-paper deleted successfully");
    } catch (error) {
      console.error("Error deleting E-paper:", error);
      toast.error("Error deleting E-paper");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate("/add-e-paper");
  };

  return {
    selectedEPaperId,
    setSelectedEPaperId,
    ePaperData,
    loading,
    handleEdit,
    handleDelete,
    handleAdd,
    setFilterModel,
    setSortingModel,
    rowCount,
    paginationModel,
    setPaginationModel,
  };
};

export default useEPaper;
